import { keyCodeToKeyName } from "components/scrapEyeLayout/hotkeys/keyCodeConversion";
import { use, useEffect } from "react";
import { useHotkeysContext } from "react-hotkeys-hook";
import { PLAYBACK_SPEEDS } from "../constants";
import { VideoPlayerGroupContext } from "../contexts/VideoPlayerGroupContext";
import { useHotkeyContext } from "../contexts/HotkeyContext";
import { useVideoControl } from "../contexts/VideoControlContext";
import { useSkipAndStepHandlers } from "./useSkipAndStepHandlers";

const isFormField = (target: EventTarget | null): boolean =>
  target instanceof HTMLInputElement ||
  target instanceof HTMLTextAreaElement ||
  target instanceof HTMLSelectElement ||
  (target instanceof HTMLElement && target.isContentEditable);

const OPEN_MENU_SELECTOR =
  '[role="listbox"], [role="menu"], [role="combobox"][aria-expanded="true"], [role="menuitem"], [role="option"]';

const isInMenu = (target: EventTarget | null): boolean =>
  target instanceof Element && target.closest(OPEN_MENU_SELECTOR) !== null;

const isRangeInput = (target: EventTarget | null): boolean =>
  target instanceof HTMLInputElement && target.type === "range";

export interface KeyboardSeek {
  stepSeconds: number;
  ceilingSeconds?: number;
}

export const useVideoKeyboardHandler = ({
  scope,
  seek,
}: {
  scope: string;
  seek: KeyboardSeek;
}) => {
  const { state, actions } = useVideoControl();
  const { getHotkey } = useHotkeyContext();
  const { enableScope, activeScopes } = useHotkeysContext();
  const { cycleAllAngles, resetAllToAuto } = use(VideoPlayerGroupContext) ?? {};
  const {
    handleSkipBackward,
    handleSkipForward,
    handleStepBackward,
    handleStepForward,
  } = useSkipAndStepHandlers({
    skipSeconds: seek.stepSeconds,
    ceilingSeconds: seek.ceilingSeconds,
  });

  useEffect(() => {
    enableScope(scope);
  }, [enableScope, scope]);
  const isHotkeysEnabled =
    activeScopes.includes(scope) || activeScopes.includes("*");

  useEffect(() => {
    const playPauseKey = getHotkey("playPause");
    const rewindLeftKey = getHotkey("rewindLeft");
    const forwardRightKey = getHotkey("forwardRight");
    const speedUpKey = getHotkey("speedUp");
    const speedDownKey = getHotkey("speedDown");
    const stepBackwardKey = getHotkey("stepBackward");
    const stepForwardKey = getHotkey("stepForward");
    const cycleAngleKey = getHotkey("cycleAngles");
    const resetAnglesToAutoKey = getHotkey("resetAnglesToAuto");

    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.defaultPrevented &&
        event.target instanceof Element &&
        event.target.matches('[role="combobox"]')
      ) {
        return;
      }

      const eventCode = keyCodeToKeyName(event.code);
      const isPlayPause = eventCode === playPauseKey;

      if (
        isFormField(event.target) &&
        !(isPlayPause && isRangeInput(event.target))
      ) {
        return;
      }

      if (isInMenu(event.target)) {
        return;
      }

      if (!isHotkeysEnabled) {
        return;
      }

      if (event.ctrlKey || event.metaKey) {
        return;
      }

      if (event.shiftKey && !(isPlayPause && event.code === "Space")) {
        return;
      }

      // Labelling holds Alt down for area mode, where the playback keys stay
      // live. The area hotkeys share the camera keys' bindings.
      const cameraKeysEnabled = !event.altKey;

      if (isPlayPause) {
        event.preventDefault();
        if (!event.repeat) actions.togglePlayPause();
      } else if (eventCode === rewindLeftKey) {
        event.preventDefault();
        handleSkipBackward();
      } else if (eventCode === forwardRightKey) {
        event.preventDefault();
        handleSkipForward();
      } else if (eventCode === speedUpKey) {
        event.preventDefault();
        const currentSpeedIndex = PLAYBACK_SPEEDS.indexOf(state.playbackRate);
        const nextSpeedIndex = Math.min(
          PLAYBACK_SPEEDS.length - 1,
          currentSpeedIndex + 1
        );
        const nextSpeed = PLAYBACK_SPEEDS[nextSpeedIndex];
        if (nextSpeed !== undefined) actions.setPlaybackRate(nextSpeed);
      } else if (eventCode === speedDownKey) {
        event.preventDefault();
        const currentSpeedIndexDown = PLAYBACK_SPEEDS.indexOf(
          state.playbackRate
        );
        const prevSpeedIndex = Math.max(0, currentSpeedIndexDown - 1);
        const prevSpeed = PLAYBACK_SPEEDS[prevSpeedIndex];
        if (prevSpeed !== undefined) actions.setPlaybackRate(prevSpeed);
      } else if (eventCode === stepBackwardKey) {
        event.preventDefault();
        handleStepBackward();
      } else if (eventCode === stepForwardKey) {
        event.preventDefault();
        handleStepForward();
      } else if (
        eventCode === cycleAngleKey &&
        cycleAllAngles &&
        cameraKeysEnabled
      ) {
        event.preventDefault();
        cycleAllAngles();
      } else if (
        eventCode === resetAnglesToAutoKey &&
        resetAllToAuto &&
        cameraKeysEnabled
      ) {
        event.preventDefault();
        resetAllToAuto();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    state.playbackRate,
    actions,
    handleSkipBackward,
    handleSkipForward,
    handleStepBackward,
    handleStepForward,
    getHotkey,
    isHotkeysEnabled,
    cycleAllAngles,
    resetAllToAuto,
  ]);
};
